import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formatISO } from 'date-fns';

import BabyEdit from '../components/BabyEdit';
import api from '../api';
import * as uiActions from '../actions/ui';
import { getAuthenticatedAccountId } from '../selectors/auth';

const formatValues = (values) => ({
  ...values,
  dueDate: values.dueDate ? formatISO(new Date(values.dueDate)) : null,
  birthDate: values.birthDate ? formatISO(new Date(values.birthDate)) : null,
  admissionDate: values.admissionDate ? formatISO(new Date(values.admissionDate)) : null
});

const BabyEditContainer = ({ baby, onBabySaved, onCancelClicked }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const accountId = useSelector(getAuthenticatedAccountId);

  useEffect(() => {
    dispatch(api.actions.babiesPatch.reset({}));
  }, [dispatch, accountId]);

  const handleAddAnother = useCallback(() => {
    dispatch(uiActions.openBabyCreateModal());
    dispatch(uiActions.closeBabyEditorModal());
  }, [dispatch]);

  const handleSubmit = useCallback((values) => {
    if (!accountId) {
      return { [import.meta.env.VITE_FORM_ERROR || 'FORM_ERROR']: "Account ID Missing" };
    }

    const formatted = formatValues(values);

    return dispatch(api.actions.babiesPatch(
      { accountId, id: baby.id },
      {
        body: JSON.stringify({
          ...formatted,
          status: 'active'
        })
      }
    ))
      .then((res) => {
        if (res && !res.error) {
          onBabySaved();
        }
        return res;
      })
      .catch((err) => {
        return { [import.meta.env.VITE_FORM_ERROR || 'FORM_ERROR']: t('accountOptions.editBabyError') };
      });
  }, [accountId, baby.id, dispatch, onBabySaved, t]);

  return (
    <BabyEdit
      initialValues={baby}
      onSubmit={handleSubmit}
      onCancelClicked={onCancelClicked}
      onAddAnotherClicked={handleAddAnother}
    />
  );
};

BabyEditContainer.propTypes = {
  onBabySaved: PropTypes.func.isRequired,
  onCancelClicked: PropTypes.func.isRequired,
  baby: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    icon: PropTypes.string
  }).isRequired
};

export default BabyEditContainer;